import { FORCED_DEFAULT_VALXML_FIELDS, MISSING_SOURCE_CONFIDENCE_CAP } from '../constants'
import type { KpiReviewProposalMetadata, KpiRow, ReviewDisagreement } from '../types'
import { canonicalizeValXmlPath, normalizeNameForComparison, normalizePathForComparison } from './valxml-builder'

export type SecondaryCheckCopy = {
    invalidJson: string
    invalidFormat: string
    missingValueReviewNote: string
    summary: {
        newEntries: (count: number) => string
        valueChanges: (count: number) => string
        proposedChanges: (count: number) => string
        confidenceReduced: (count: number) => string
        join: (parts: string[]) => string
        noChanges: string
    }
}

export const DEFAULT_SECONDARY_CHECK_COPY: SecondaryCheckCopy = {
    invalidJson: 'Die Prüfantwort war kein gültiges JSON. Bitte KI-Antwort prüfen oder den Prüfvorgang erneut starten.',
    invalidFormat: 'Die Prüfantwort war nicht im erwarteten JSON-Format.',
    missingValueReviewNote: 'Fehlender Wert laut KI-Prüfung.',
    summary: {
        newEntries: (count) => `${count} ${count === 1 ? 'neuen Eintrag' : 'neue Einträge'} hinzugefügt`,
        valueChanges: (count) => `Werte bei ${count} ${count === 1 ? 'Eintrag' : 'Einträgen'} aktualisiert`,
        proposedChanges: (count) => `${count} ${count === 1 ? 'Eintrag' : 'Einträge'} mit Änderungsvorschlag markiert`,
        confidenceReduced: (count) => `Confidence für ${count} ${count === 1 ? 'Eintrag' : 'Einträge'} reduziert`,
        join: (parts) => parts.join(' und '),
        noChanges: 'Zusätzliche Prüfung abgeschlossen – keine Änderungen.',
    },
}

const normalizeValueForComparison = (value?: string | null): string => {
    if (!value) {
        return ''
    }
    const trimmed = value.trim()
    if (!trimmed) {
        return ''
    }
    if (trimmed === '-' || trimmed === '—' || trimmed === '–') {
        return ''
    }
    return trimmed
}

const CALCULATION_KEYWORD_PATTERN =
    // eslint-disable-next-line max-len
    /\b(berechnet|berechnung|errechnet|rechnerisch|calculated|calculation|computed|deriviert|derived|abgeleitet|geschätzt|geschaetzt|estimated|estimate|approx|approximation|prognose|projection|projiziert|projected|interpoliert|interpolated|kalkuliert|kalkulation)\b/i

const containsCalculationKeyword = (input?: string | null): boolean => {
    if (!input) {
        return false
    }
    return CALCULATION_KEYWORD_PATTERN.test(input)
}

const toProposalMetadata = (disagreement: ReviewDisagreement): KpiReviewProposalMetadata => {
    const sourceText = disagreement.sourceText?.trim() || undefined
    const notes = disagreement.notes?.trim() || undefined
    const hasDirectSource = Boolean(sourceText)

    return {
        unit: disagreement.unit?.trim() || undefined,
        notes,
        page:
            typeof disagreement.page === 'number' && Number.isFinite(disagreement.page) && disagreement.page > 0 ? disagreement.page : null,
        sourceText,
        confidence:
            typeof disagreement.confidence === 'number' && Number.isFinite(disagreement.confidence)
                ? Math.min(Math.max(disagreement.confidence, 0), 1)
                : undefined,
        hasDirectSource,
        isCalculated: !hasDirectSource || containsCalculationKeyword(notes) || containsCalculationKeyword(disagreement.reason),
    }
}

export const getAcceptedReviewProposalUpdates = (row: KpiRow): Partial<KpiRow> | null => {
    const proposedValue = row.proposedValue?.trim()
    if (!proposedValue) {
        return null
    }

    const metadata = row.proposedMetadata
    return {
        value: proposedValue,
        unit: metadata?.unit,
        notes: metadata?.notes,
        page: metadata?.page ?? null,
        sourceText: metadata?.sourceText,
        confidence: metadata?.confidence,
        hasDirectSource: metadata?.hasDirectSource ?? false,
        isCalculated: metadata?.isCalculated ?? true,
        usedCodeInterpreter: false,
        proposedValue: null,
        proposedMetadata: null,
        reviewDisagreed: false,
        reviewNotes: undefined,
        needsReview: false,
        lastReviewRound: undefined,
    }
}

const buildSummaryMessage = (
    valueChanges: number,
    confidenceReduced: number,
    newEntries: number,
    proposedChanges: number,
    copy: SecondaryCheckCopy
): string => {
    const parts: string[] = []
    if (newEntries > 0) {
        parts.push(copy.summary.newEntries(newEntries))
    }
    if (valueChanges > 0) {
        parts.push(copy.summary.valueChanges(valueChanges))
    }
    if (proposedChanges > 0) {
        parts.push(copy.summary.proposedChanges(proposedChanges))
    }
    if (confidenceReduced > 0) {
        parts.push(copy.summary.confidenceReduced(confidenceReduced))
    }
    return parts.length > 0 ? copy.summary.join(parts) : copy.summary.noChanges
}

export type SecondaryCheckSuccess = {
    status: 'success'
    disagreements: ReviewDisagreement[]
    updatedRows: KpiRow[]
    summaryMessage: string
}

export type SecondaryCheckFailure = {
    status: 'error'
    errorMessage: string
}

export type SecondaryCheckResult = SecondaryCheckSuccess | SecondaryCheckFailure

export const applySecondaryCheckDisagreements = ({
    disagreements,
    currentRows,
    copy = DEFAULT_SECONDARY_CHECK_COPY,
}: {
    disagreements: ReviewDisagreement[]
    currentRows: KpiRow[]
    copy?: SecondaryCheckCopy
}): SecondaryCheckSuccess => {
    const disagreementByName = new Map<string, ReviewDisagreement>()
    const disagreementByPath = new Map<string, ReviewDisagreement>()
    const matchedDisagreements = new Set<ReviewDisagreement>()

    disagreements.forEach((entry) => {
        const normalizedName = normalizeNameForComparison(entry.name)
        if (normalizedName) {
            disagreementByName.set(normalizedName, entry)
        }
        const normalizedPath = normalizePathForComparison(entry.valXmlPath)
        if (normalizedPath) {
            disagreementByPath.set(normalizedPath, entry)
        }
    })

    const forcedDefaultPathSet = new Set(FORCED_DEFAULT_VALXML_FIELDS.map((path) => normalizePathForComparison(path) ?? path.toLowerCase()))

    let valueChangesCount = 0
    let proposedChangesCount = 0
    let confidenceReducedCount = 0

    const updatedRows = currentRows.map((row) => {
        const nameKey = normalizeNameForComparison(row.name)
        const pathKey = normalizePathForComparison(row.valXmlPath)
        const disagreementFromPath = pathKey ? disagreementByPath.get(pathKey) : undefined
        const disagreementFromName = nameKey ? disagreementByName.get(nameKey) : undefined
        const disagreement = disagreementFromPath ?? disagreementFromName

        const nextRow: KpiRow = {
            ...row,
            needsReview: false,
            reviewNotes: row.reviewNotes,
            reviewDisagreed: Boolean(row.reviewDisagreed),
            lastReviewRound: undefined,
            proposedValue: null,
            proposedMetadata: null,
        }

        const originalValueNormalized = normalizeValueForComparison(row.value)
        const originalConfidence = typeof row.confidence === 'number' ? row.confidence : undefined

        let valueDisagreement = false
        let shouldReplaceValue = false

        if (disagreement) {
            matchedDisagreements.add(disagreement)
            if ((!row.valXmlPath || row.valXmlPath.trim().length === 0) && disagreement.valXmlPath) {
                const canonicalPath = canonicalizeValXmlPath(disagreement.valXmlPath)
                if (canonicalPath) {
                    nextRow.valXmlPath = canonicalPath
                }
            }

            const suggestedValueRaw = typeof disagreement.value === 'string' ? disagreement.value.trim() : ''
            const hasMeaningfulValue = suggestedValueRaw.length > 0 && suggestedValueRaw !== '-' && suggestedValueRaw !== '—'
            const normalizedSuggested = hasMeaningfulValue ? normalizeValueForComparison(suggestedValueRaw) : ''

            shouldReplaceValue = hasMeaningfulValue && normalizedSuggested.length > 0 && originalValueNormalized.length === 0
            const shouldProposeValue =
                hasMeaningfulValue &&
                normalizedSuggested.length > 0 &&
                originalValueNormalized.length > 0 &&
                normalizedSuggested !== originalValueNormalized

            valueDisagreement = shouldReplaceValue || shouldProposeValue

            if (shouldReplaceValue) {
                nextRow.value = suggestedValueRaw
                nextRow.proposedValue = null
                const metadata = toProposalMetadata(disagreement)
                nextRow.unit = metadata.unit
                nextRow.notes = metadata.notes
                nextRow.page = metadata.page
                nextRow.sourceText = metadata.sourceText
                nextRow.hasDirectSource = metadata.hasDirectSource
                nextRow.isCalculated = metadata.isCalculated
                nextRow.confidence = metadata.confidence
                nextRow.usedCodeInterpreter = false
            } else if (shouldProposeValue) {
                nextRow.proposedValue = suggestedValueRaw
                nextRow.proposedMetadata = toProposalMetadata(disagreement)
            }

            if (!valueDisagreement && disagreement.unit && (!row.unit || row.unit.trim().length === 0)) {
                nextRow.unit = disagreement.unit
            }
            if (!valueDisagreement && disagreement.notes && (!row.notes || row.notes.trim().length === 0)) {
                nextRow.notes = disagreement.notes
            }
            if (!valueDisagreement && disagreement.sourceText && (!row.sourceText || row.sourceText.trim().length === 0)) {
                nextRow.sourceText = disagreement.sourceText
            }
            if (!valueDisagreement && typeof disagreement.page === 'number' && (row.page === undefined || row.page === null)) {
                nextRow.page = disagreement.page
            }

            if (valueDisagreement) {
                if (typeof row.confidence === 'number' && row.confidence < 0) {
                    nextRow.confidence = row.confidence
                } else {
                    const baseConfidence =
                        typeof row.confidence === 'number'
                            ? row.confidence
                            : typeof disagreement.confidence === 'number'
                              ? disagreement.confidence
                              : undefined

                    nextRow.confidence = typeof baseConfidence === 'number' ? Math.min(Math.max(baseConfidence * 0.5, 0), 1) : 0.25
                    if (typeof disagreement.confidence === 'number') {
                        nextRow.confidence = Math.min(Math.max(disagreement.confidence * 0.5, 0), 1)
                    }
                }
            }

            if (shouldProposeValue) {
                proposedChangesCount += 1
            }
        }

        if (valueDisagreement) {
            nextRow.reviewDisagreed = true
            nextRow.reviewNotes = disagreement?.reason
        } else {
            nextRow.reviewDisagreed = Boolean(row.reviewDisagreed)
            nextRow.reviewNotes = row.reviewNotes
        }

        const hasDirectSourceAfterUpdate = typeof nextRow.sourceText === 'string' && nextRow.sourceText.trim().length > 0

        if (valueDisagreement) {
            const calculationHintFromNotes =
                containsCalculationKeyword(nextRow.reviewNotes) || containsCalculationKeyword(disagreement?.reason)
            const calculatedAfterUpdate = !hasDirectSourceAfterUpdate || calculationHintFromNotes
            nextRow.hasDirectSource = hasDirectSourceAfterUpdate
            nextRow.isCalculated = calculatedAfterUpdate
            if (!hasDirectSourceAfterUpdate) {
                nextRow.confidence =
                    typeof nextRow.confidence === 'number'
                        ? Math.min(nextRow.confidence, MISSING_SOURCE_CONFIDENCE_CAP)
                        : MISSING_SOURCE_CONFIDENCE_CAP
            }
        } else {
            nextRow.hasDirectSource = row.hasDirectSource ?? hasDirectSourceAfterUpdate
            nextRow.isCalculated = row.isCalculated ?? false
        }

        const nextValueNormalized = normalizeValueForComparison(nextRow.value)
        const valueChangedThisRound =
            shouldReplaceValue && nextValueNormalized !== originalValueNormalized && nextValueNormalized.length > 0
        if (valueChangedThisRound) {
            valueChangesCount += 1
        }

        if (
            disagreement &&
            typeof originalConfidence === 'number' &&
            typeof nextRow.confidence === 'number' &&
            nextRow.confidence < originalConfidence
        ) {
            confidenceReducedCount += 1
        }

        return nextRow
    })

    const additionalRows: KpiRow[] = []
    disagreements.forEach((entry) => {
        if (matchedDisagreements.has(entry)) {
            return
        }
        const normalizedPath = normalizePathForComparison(entry.valXmlPath)
        const canonicalPath = canonicalizeValXmlPath(entry.valXmlPath)
        if (normalizedPath && forcedDefaultPathSet.has(normalizedPath)) {
            return
        }

        const displayName = entry.name?.trim().length ? entry.name.trim() : (entry.valXmlPath ?? 'Unbekannter KPI')
        const suggestedValue = entry.value?.trim()
        const valueForRow = suggestedValue && suggestedValue !== '-' && suggestedValue !== '—' ? suggestedValue : ''
        const metadata = toProposalMetadata(entry)
        const confidenceForRow = metadata.confidence ?? 0.1
        let effectiveConfidence = Math.min(Math.max(confidenceForRow * 0.5, 0), 1)
        if (!metadata.hasDirectSource) {
            effectiveConfidence = Math.min(effectiveConfidence, MISSING_SOURCE_CONFIDENCE_CAP)
        }
        additionalRows.push({
            name: displayName,
            value: valueForRow,
            unit: metadata.unit,
            notes: metadata.notes,
            page: metadata.page,
            sourceText: metadata.sourceText,
            hasDirectSource: metadata.hasDirectSource,
            isCalculated: metadata.isCalculated,
            valXmlPath: canonicalPath ?? undefined,
            confidence: effectiveConfidence,
            needsReview: false,
            reviewNotes: entry.reason ?? copy.missingValueReviewNote,
            lastReviewRound: undefined,
            reviewDisagreed: false,
            proposedValue: null,
            proposedMetadata: null,
        })
    })

    const nextRows = [...updatedRows, ...additionalRows]

    const summaryMessage = buildSummaryMessage(valueChangesCount, confidenceReducedCount, additionalRows.length, proposedChangesCount, copy)

    return {
        status: 'success',
        disagreements,
        updatedRows: nextRows,
        summaryMessage,
    }
}
